import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import Close from "../../../assets/icons/Close.svg";
import { FiPlus, FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../../redux/slices/modalSlice";
import { clearChatPoll, createChatPoll } from "../../../redux/slices/pollSlice";

const MAX_OPTIONS = 6;
const SHOW_RESULTS = [
  { key: "afterVote", label: "After voting" },
  { key: "always", label: "Always" },
  { key: "adminOnly", label: "Admin only" },
];

const PollModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.modals.modalPollOpen);
  const poll = useSelector((state) => state.poll);
  const status = useSelector((state) => state.poll.status);

  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [showResults, setShowResults] = useState("afterVote");
  const [error, setError] = useState("");

  const reset = () => {
    setQuestion("");
    setOptions(["", ""]);
    setShowResults("afterVote");
    setError("");
  };

  const handleClose = () => {
    dispatch(closeModal("modalPollOpen"));
    dispatch(clearChatPoll());
    reset();
  };

  const setOption = (i, value) =>
    setOptions((prev) => prev.map((o, idx) => (idx === i ? value : o)));
  const addOption = () =>
    setOptions((prev) => (prev.length < MAX_OPTIONS ? [...prev, ""] : prev));
  const removeOption = (i) =>
    setOptions((prev) =>
      prev.length > 2 ? prev.filter((_, idx) => idx !== i) : prev
    );

  const cleanedOptions = options.map((o) => o.trim()).filter(Boolean);
  const isValid =
    question.trim() !== "" &&
    cleanedOptions.length >= 2 &&
    new Set(cleanedOptions.map((o) => o.toLowerCase())).size ===
      cleanedOptions.length;

  const handleCreate = () => {
    setError("");
    if (question.trim() === "") {
      setError("Add a question.");
      return;
    }
    if (cleanedOptions.length < 2) {
      setError("Add at least two options.");
      return;
    }
    if (
      new Set(cleanedOptions.map((o) => o.toLowerCase())).size !==
      cleanedOptions.length
    ) {
      setError("Options must be unique.");
      return;
    }
    if (!poll.topic) {
      setError("Missing topic. Reopen the poll from the chat.");
      return;
    }
    dispatch(
      createChatPoll({
        question: question.trim(),
        choices: cleanedOptions,
        topic: poll.topic,
        showResults,
      })
    )
      .unwrap()
      .then(() => handleClose())
      .catch((err) => {
        setError(typeof err === "string" ? err : err?.message || "Could not create poll.");
      });
  };

  if (!isOpen) return null;

  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-70 z-40" />
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <Dialog.Content className="bg-theme-tertiaryBackground rounded-xl overflow-hidden shadow-xl w-full sm:w-[28rem] max-h-[90vh] overflow-y-auto custom-scrollbar">
            <Dialog.Title />
            <div className="flex flex-col p-5">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-normal text-theme-secondaryText font-inter">
                  Create a poll
                </h2>
                <img
                  src={Close}
                  alt="Close"
                  className="h-4 w-4 cursor-pointer"
                  onClick={handleClose}
                />
              </div>

              {/* Question */}
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
                Question
              </label>
              <input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                maxLength={140}
                placeholder="Ask something…"
                className="w-full rounded-lg border border-theme-chatDivider bg-transparent px-3 py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
              />

              {/* Options */}
              <label className="mb-1.5 mt-4 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
                Options
              </label>
              <div className="flex flex-col gap-2">
                {options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={opt}
                      onChange={(e) => setOption(i, e.target.value)}
                      maxLength={80}
                      placeholder={`Option ${i + 1}`}
                      className="w-full rounded-lg border border-theme-chatDivider bg-transparent px-3 py-2 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
                    />
                    {options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(i)}
                        className="flex-shrink-0 rounded-md p-1.5 text-theme-placeholder hover:bg-theme-primaryBackground hover:text-theme-error"
                        aria-label="Remove option"
                      >
                        <FiX size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {options.length < MAX_OPTIONS && (
                <button
                  type="button"
                  onClick={addOption}
                  className="mt-2 flex items-center gap-1.5 text-sm font-medium text-theme-accent"
                >
                  <FiPlus size={15} /> Add option
                </button>
              )}

              {/* Show results */}
              <label className="mb-2 mt-4 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
                Show results
              </label>
              <div className="inline-flex rounded-lg border border-theme-chatDivider p-1">
                {SHOW_RESULTS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setShowResults(s.key)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      showResults === s.key
                        ? "bg-theme-accent text-theme-accentText"
                        : "text-theme-primaryText hover:bg-theme-primaryBackground"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {error && (
                <p className="mt-3 text-sm font-light text-theme-error">{error}</p>
              )}

              <button
                onClick={handleCreate}
                disabled={!isValid || status === "loading"}
                className={`mt-5 w-full rounded-lg py-2.5 text-sm font-normal ${
                  !isValid || status === "loading"
                    ? "bg-theme-buttonDisable text-theme-buttonDisableText opacity-60"
                    : "bg-theme-secondaryText text-theme-primaryBackground"
                }`}
              >
                {status === "loading" ? "Creating…" : "Create poll"}
              </button>
            </div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PollModal;
